<template>
  <sfs-base-list-filter
    title="Filter Commissions"
    :table-name="tableName"
    :applied-filters="appliedFilters"
    :displayed-columns="displayedColumns"
    :available-columns="availableColumns"
    :column-picker="false"
    @filters-applied="onFiltersApplied"
    @filters-reset="onDefaultPresetApplied"
    @filter-cleared="onFilterCleared"
    :advanced-filter="false"
    :popover-filter="true"
  >
    <template #quick-filters>
      <b-form-group
        :label="fieldLabels.dates"
        label-for="quick-filter-dates"
        label-sr-only
      >
        <div class="d-flex">
          <sfs-date-range-picker
            id="quick-filter-dates"
            :value="dates"
            placeholder="Select Dates"
            @on-change="onDatesChange"
          />
          <b-input-group-append>
            <b-button
              id="quick-filter-apply-button"
              @click="applyFilters"
              variant="primary"
            >
              <i class="fa fa-search" />
            </b-button>
          </b-input-group-append>
        </div>
      </b-form-group>
    </template>

    <template #popover-filters>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="User"
            label-for="popover-filter-user_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <employee-select
              id="popover-filter-user_id"
              placeholder="All"
              :value="user"
              @input="onUserChange"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            label="Commission Plan"
            label-for="popover-filter-commission_plan_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <commission-plan-select
              id="popover-filter-commission_plan_id"
              placeholder="Commission Plan"
              :value="commissionPlan"
              @input="onCommissionPlanChange"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            label="Client"
            label-for="popover-filter-client_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <client-select
              id="popover-filter-client_id"
              placeholder="All"
              :value="client"
              @input="onClientChange"
              :persist-state="true"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.location_id"
            label-for="popover-filter-location_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <location-select
              id="popover-filter-location_id"
              :client-id="filters.client_id"
              placeholder="All"
              :value="location"
              @input="onLocationChange"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.service_id"
            label-for="popover-filter-service_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <service-select
              id="popover-filter-service_id"
              placeholder="Service"
              :value="service"
              @input="onServiceChange"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </template>

    <template #right-tray>
      <sfs-download-button
        v-b-tooltip.hover
        :provider-params="filters"
        filename="Commissions Report.csv"
        :file-provider="$analyticsAPI.getCommissionsReportSpreadsheet"
        title="Download spreadsheet"
      />
    </template>
  </sfs-base-list-filter>
</template>
<script>
import LocationSelect from '@/components/admin/locations/Select.vue';
import ClientSelect from '@/components/admin/clients/Select.vue';
import ServiceSelect from '@/components/admin/services/Select.vue';
import EmployeeSelect from '@/components/admin/users/EmployeeSelect.vue';
import CommissionPlanSelect from '@/components/admin/commissionPlans/Select.vue';
import listFilterMixin from '@/mixins/listFilterMixin';

import {
  WORK_ORDER_FORM_LABELS,
} from '@/constants/workOrders';
import { COMMISSION_REPORT_TABLE_NAME, COMMISSION_REPORT_TABLE_DEFAULT_COLUMNS, COMMISSION_REPORT_TABLE_COLUMN_OPTIONS } from '@/constants/reports';
export default {
  name: 'WorkOrderListFilter',
  mixins: [listFilterMixin],
  components: {
    LocationSelect,
    ClientSelect,
    ServiceSelect,
    EmployeeSelect,
    CommissionPlanSelect,
  },
  data() {
    return {
      fieldLabels: WORK_ORDER_FORM_LABELS,
      tableName: COMMISSION_REPORT_TABLE_NAME,
      availableColumns: COMMISSION_REPORT_TABLE_COLUMN_OPTIONS,
    };
  },
  mounted() {
    this.loadInitialFilters();
  },
  methods: {
    applyPresetFilter(filters, columns) {
      this.filters = filters;
      this.displayedColumns = columns;
      this.applyFilters();
    },
    onDefaultPresetApplied() {
      this.applyPresetFilter({}, COMMISSION_REPORT_TABLE_DEFAULT_COLUMNS);
    },
    onLocationChange(location) {
      this.filters.location_id = location ? location.id.toString() : '';
      this.location = location;
    },
    onClientChange(client) {
      this.filters.client_id = client ? client.id.toString() : '';
      this.client = client;
    },
    onUserChange(user) {
      this.filters.user_id = user ? user.id.toString() : '';
      this.user = user;
    },
    onServiceChange(service) {
      this.filters.service_id = service ? service.id.toString() : '';
      this.service = service;
    },
    onCommissionPlanChange(commissionPlan) {
      this.filters.commission_plan_id = commissionPlan ? commissionPlan.id.toString() : '';
      this.commissionPlan = commissionPlan;
    },
    onDatesChange(dates) {
      if (dates[0] && dates[1]) {
        this.filters.start_date = dates[0] ? this.$options.filters.dateFormat(dates[0].toISOString()) : '';
        this.filters.end_date = dates[1] ? this.$options.filters.dateFormat(dates[1].toISOString()) : '';
        this.applyFilters();
      }
    },
    downloadSpreadsheet() {
      this.download(this.$workOrdersAPI.getSpreadsheet, this.filters, 'Commissions.csv');
    },
    loadInitialFilters() {
      const loadLocation = new Promise((resolve) => {
        if (this.initialFilters.location_id) {
          this.$locationsAPI.get(this.initialFilters.location_id).then((location) => {
            resolve(location);
          });
        } else {
          resolve(null);
        }
      });

      const loadClient = new Promise((resolve) => {
        if (this.initialFilters.client_id) {
          this.$clientsAPI.get(this.initialFilters.client_id).then((client) => {
            resolve(client);
          });
        } else {
          resolve(null);
        }
      });

      const loadService = new Promise((resolve) => {
        if (this.initialFilters.service_id) {
          this.$servicesAPI.get(this.initialFilters.service_id).then((service) => {
            resolve(service);
          });
        } else {
          resolve(null);
        }
      });

      const loadUser = new Promise((resolve) => {
        if (this.initialFilters.user_id) {
          this.$usersAPI.get(this.initialFilters.user_id).then((user) => {
            resolve(user);
          });
        } else {
          resolve(null);
        }
      });

      const loadCommissionPlan = new Promise((resolve) => {
        if (this.initialFilters.commission_plan_id) {
          this.$commissionPlansAPI.get(this.initialFilters.commission_plan_id).then((commissionPlan) => {
            resolve(commissionPlan);
          });
        } else {
          resolve(null);
        }
      });
      // The order of the resolved promises is preserved so we are able to use
      // indexes to fetch the data.
      Promise.all([loadLocation, loadClient, loadService, loadUser, loadCommissionPlan]).then((values) => {
        this.location = values[0];
        this.client = values[1];
        this.service = values[2];
        this.user = values[3];
        this.commissionPlan = values[4];
        this.applyFilters();
      });
    },
  },
  computed: {
    dates() {
      return [this.filters.start_date, this.filters.end_date];
    },
  },
};
</script>
